import { useState, useEffect, useRef } from 'react';
import { Search, MessageSquare, Book, Mail, HelpCircle, Zap, Shield, CreditCard, Users, Send, ArrowLeft, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Page Knowledge Base avec IA Botpress
 * Interface de conversation style ChatGPT avec transformations d'éléments
 */
export default function KnowledgeBase() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [chatbotLoaded, setChatbotLoaded] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showContactPopup, setShowContactPopup] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Questions fréquentes sous forme de tuiles
  const frequentQuestions = [
    {
      icon: <CreditCard className="w-6 h-6" />,
      title: "Comparaison des plans",
      question: "Quelle différence entre Starter et Pro ?",
      color: "blue"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Sécurité des données",
      question: "Comment sont protégées mes données RNCP ?",
      color: "green"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Réduction association",
      question: "Comment obtenir les 30% de réduction ?",
      color: "purple"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Changement de plan",
      question: "Puis-je changer de plan à tout moment ?",
      color: "orange"
    },
    {
      icon: <Book className="w-6 h-6" />,
      title: "Limite formations",
      question: "Que se passe-t-il si je dépasse ma limite ?",
      color: "indigo"
    },
    {
      icon: <HelpCircle className="w-6 h-6" />,
      title: "Facturation annuelle",
      question: "Combien j'économise avec l'abonnement annuel ?",
      color: "pink"
    }
  ];

  // Auto-scroll vers le bas des messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Marquer chatbot comme chargé
  useEffect(() => {
    setChatbotLoaded(true);
  }, []);

  // Démarrer une conversation avec une question prédéfinie
  const startChatWithQuestion = (question) => {
    setShowChat(true);

    // Ajouter le message utilisateur
    setMessages([{
      type: 'user',
      text: question,
      timestamp: new Date()
    }]);

    // Simuler réponse IA (vous pouvez remplacer par appel API Botpress)
    setTimeout(() => {
      sendToBotpress(question);
    }, 500);
  };

  // Envoyer un message à Botpress et récupérer la réponse
  const sendToBotpress = async (userMessage) => {
    setIsTyping(true);

    try {
      // Pour l'instant, simulation de réponse
      // TODO: Remplacer par vrai appel API Botpress
      await new Promise(resolve => setTimeout(resolve, 1500));

      const botResponse = generateBotResponse(userMessage);

      setMessages(prev => [...prev, {
        type: 'bot',
        text: botResponse,
        timestamp: new Date()
      }]);
    } catch (error) {
      console.error('Erreur Botpress:', error);
      setMessages(prev => [...prev, {
        type: 'bot',
        text: "Désolé, une erreur s'est produite. Pouvez-vous reformuler votre question ?",
        timestamp: new Date()
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  // Générer réponse bot (simulation - à remplacer par vraie API)
  const generateBotResponse = (question) => {
    const lowerQuestion = question.toLowerCase();

    if (lowerQuestion.includes('starter') || lowerQuestion.includes('pro') || lowerQuestion.includes('différence')) {
      return `**Comparaison Starter vs Pro**\n\n**Plan Starter (29€/mois):**\n- Jusqu'à 50 formations\n- Analyse RNCP de base\n- Support email\n\n**Plan Pro (99€/mois):**\n- Formations illimitées\n- Analyse RNCP avancée + blocs de compétences\n- Support prioritaire\n- Export de données\n- Multi-utilisateurs\n\nLe plan Pro est recommandé pour les organismes avec plus de 50 formations ou des besoins d'analyse poussés. Souhaitez-vous plus de détails sur l'un des plans ?`;
    }

    if (lowerQuestion.includes('rgpd') || lowerQuestion.includes('sécurité') || lowerQuestion.includes('données')) {
      return `**Sécurité et RGPD**\n\nVos données RNCP sont protégées par :\n- 🔒 Chiffrement AES-256\n- 🇫🇷 Hébergement en France (OVH)\n- ✅ Conformité RGPD totale\n- 🚫 Aucune revente de données\n- 🔐 Authentification sécurisée\n\nVous gardez la propriété totale de vos données. Vous pouvez les exporter ou supprimer à tout moment. Des questions sur nos pratiques de sécurité ?`;
    }

    if (lowerQuestion.includes('association') || lowerQuestion.includes('réduction') || lowerQuestion.includes('30%')) {
      return `**Réduction Association (30%)**\n\nLes associations à but non lucratif bénéficient de -30% sur tous nos plans :\n- Plan Starter: **20,30€/mois** au lieu de 29€\n- Plan Pro: **69,30€/mois** au lieu de 99€\n\n**Comment en bénéficier ?**\n1. Contacter notre support\n2. Fournir preuve statut associatif (JO, Siret)\n3. Réduction appliquée sous 48h\n\nSouhaitez-vous être mis en relation avec notre équipe ?`;
    }

    return `Merci pour votre question ! Je suis là pour vous aider concernant Match Compétences.\n\nJe peux vous renseigner sur :\n- 📊 Les plans tarifaires (Starter vs Pro)\n- 🔒 La sécurité des données RNCP\n- 💰 Les réductions associations\n- 🔄 Le changement de plan\n- 📈 Les limites de formations\n\nQue souhaitez-vous savoir exactement ?`;
  };

  // Envoyer un message utilisateur
  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const userMsg = inputMessage.trim();
    setInputMessage('');

    setMessages(prev => [...prev, {
      type: 'user',
      text: userMsg,
      timestamp: new Date()
    }]);

    sendToBotpress(userMsg);
  };

  // Démarrer conversation vide
  const startEmptyChat = () => {
    setShowChat(true);
    setMessages([{
      type: 'bot',
      text: "👋 Bonjour ! Je suis l'assistant Match Compétences. Comment puis-je vous aider aujourd'hui ?",
      timestamp: new Date()
    }]);
  };

  // Recherche - démarre chat avec requête
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      startChatWithQuestion(searchQuery);
      setSearchQuery('');
    }
  };

  // Retour à l'accueil
  const handleBackToHome = () => {
    setShowChat(false);
    setMessages([]);
    setInputMessage('');
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: 'from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700',
      green: 'from-green-500 to-green-600 hover:from-green-600 hover:to-green-700',
      purple: 'from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700',
      orange: 'from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700',
      indigo: 'from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700',
      pink: 'from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 relative overflow-hidden">
      {/* Header fixe */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-900 flex items-center mb-4"
          >
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Retour
          </button>
        </div>
      </div>

      {/* Bouton retour - apparaît en haut à gauche quand chat actif */}
      {showChat && (
        <button
          onClick={handleBackToHome}
          className="fixed top-24 left-6 z-50 p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 animate-fade-in"
        >
          <ArrowLeft className="w-6 h-6 text-gray-700" />
        </button>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative">
        {/* Hero Section - Disparaît vers le haut */}
        <div
          className={`text-center mb-16 transition-all duration-700 ease-in-out ${
            showChat
              ? 'opacity-0 -translate-y-[200%] pointer-events-none'
              : 'opacity-100 translate-y-0'
          }`}
        >
          <div className="flex items-center justify-center mb-6">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-full p-4">
              <MessageSquare className="w-12 h-12 text-white" />
            </div>
          </div>

          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Centre d'aide
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Notre assistant IA est là pour répondre à toutes vos questions sur Match Compétences
          </p>

          {/* Badge de statut du chatbot */}
          {chatbotLoaded && (
            <div className="mt-8">
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-green-100 text-green-800">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
                Assistant IA en ligne
              </span>
            </div>
          )}
        </div>

        {/* Barre de recherche - Se transforme et va en bas */}
        <div
          className={`mb-16 transition-all duration-700 ease-in-out ${
            showChat
              ? 'fixed bottom-0 left-0 right-0 z-40 mb-0 px-6 py-4 bg-white border-t border-gray-200'
              : 'relative'
          }`}
        >
          <form
            onSubmit={showChat ? handleSendMessage : handleSearch}
            className={`mx-auto transition-all duration-700 ${showChat ? 'max-w-4xl' : 'max-w-3xl'}`}
          >
            <div className="relative flex items-center gap-4">
              {!showChat && (
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Search className="h-6 w-6 text-gray-400" />
                </div>
              )}
              <input
                type="text"
                value={showChat ? inputMessage : searchQuery}
                onChange={(e) => showChat ? setInputMessage(e.target.value) : setSearchQuery(e.target.value)}
                placeholder={showChat ? "Posez votre question..." : "Rechercher dans la base de connaissances..."}
                className={`block w-full ${showChat ? 'pl-6' : 'pl-12 pr-32'} py-${showChat ? '4' : '5'} text-lg border-2 border-gray-300 rounded-2xl focus:ring-4 focus:ring-blue-500 focus:border-blue-500 shadow-lg transition-all`}
                disabled={showChat && isTyping}
              />
              <button
                type="submit"
                disabled={showChat ? (!inputMessage.trim() || isTyping) : !searchQuery.trim()}
                className={`${showChat ? 'relative' : 'absolute right-2 top-1/2 transform -translate-y-1/2'} bg-gradient-to-r from-blue-600 to-purple-600 text-white ${showChat ? 'p-4' : 'px-6 py-3'} rounded-${showChat ? '2xl' : 'xl'} font-semibold hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl`}
              >
                {showChat ? <Send className="w-6 h-6" /> : 'Rechercher'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              💬 Propulsé par IA • {showChat ? 'Conversations 100% anonymes • Conforme RGPD' : 'Réponses instantanées • Conversations 100% anonymes'}
            </p>
          </form>
        </div>

        {/* Questions fréquentes - Disparaissent vers le bas */}
        <div
          className={`mb-16 transition-all duration-700 ease-in-out ${
            showChat
              ? 'opacity-0 translate-y-[200%] pointer-events-none'
              : 'opacity-100 translate-y-0'
          }`}
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
            Questions fréquentes
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {frequentQuestions.map((item, index) => (
              <button
                key={index}
                onClick={() => startChatWithQuestion(item.question)}
                className={`bg-gradient-to-r ${getColorClasses(item.color)} text-white rounded-2xl p-6 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all text-left`}
              >
                <div className="bg-white/20 rounded-full w-14 h-14 flex items-center justify-center mb-4">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-white/90">{item.question}</p>
              </button>
            ))}
          </div>
        </div>

        {/* CTA Contact - Se transforme en bulle flottante */}
        <div
          className={`transition-all duration-700 ease-in-out ${
            showChat
              ? 'fixed bottom-24 right-6 z-40 w-auto'
              : 'relative w-full'
          }`}
        >
          {showChat ? (
            <button
              onClick={() => setShowContactPopup(true)}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 rounded-full shadow-2xl hover:shadow-xl hover:scale-110 transition-all animate-fade-in"
            >
              <Mail className="w-6 h-6" />
            </button>
          ) : (
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-3xl shadow-2xl p-12 text-center text-white">
              <Mail className="w-16 h-16 mx-auto mb-6 text-blue-400" />
              <h2 className="text-3xl font-bold mb-4">
                Vous ne trouvez pas votre réponse ?
              </h2>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Notre équipe support est disponible pour vous aider personnellement
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/contact')}
                  className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 text-white rounded-xl font-semibold text-lg hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Contacter le support
                </button>

                <button
                  onClick={startEmptyChat}
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all shadow-lg"
                >
                  <MessageSquare className="w-5 h-5 mr-2" />
                  Discuter avec l'IA
                </button>
              </div>

              <p className="text-sm text-gray-400 mt-6">
                🔒 Conforme RGPD • Réponse sous 24h • Support en français
              </p>
            </div>
          )}
        </div>

        {/* Zone de messages - Apparaît au centre quand chat actif */}
        {showChat && (
          <div
            className="fixed top-32 left-0 right-0 bottom-32 overflow-y-auto px-6 py-8 space-y-6 animate-fade-in"
            ref={chatContainerRef}
          >
            <div className="max-w-4xl mx-auto">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} mb-6 animate-fade-in`}
                >
                  <div className={`max-w-3xl ${message.type === 'user' ? 'ml-12' : 'mr-12'}`}>
                    {message.type === 'bot' && (
                      <div className="flex items-center gap-2 mb-2">
                        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-full w-8 h-8 flex items-center justify-center text-white font-bold text-sm">
                          AI
                        </div>
                        <span className="text-sm text-gray-500 font-medium">Assistant</span>
                      </div>
                    )}
                    <div
                      className={`rounded-2xl px-6 py-4 ${
                        message.type === 'user'
                          ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                          : 'bg-white text-gray-900 border border-gray-200'
                      } shadow-md`}
                    >
                      <div className="whitespace-pre-wrap leading-relaxed">
                        {message.text.split('\n').map((line, i) => {
                          if (line.startsWith('**') && line.endsWith('**')) {
                            return <div key={i} className="font-bold text-lg mb-2">{line.replace(/\*\*/g, '')}</div>;
                          }
                          if (line.startsWith('- ')) {
                            return <div key={i} className="ml-4">• {line.substring(2)}</div>;
                          }
                          return <div key={i}>{line}</div>;
                        })}
                      </div>
                    </div>
                    {message.type === 'user' && (
                      <div className="flex items-center justify-end gap-2 mt-2">
                        <span className="text-sm text-gray-500">Vous</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Indicateur de saisie */}
              {isTyping && (
                <div className="flex justify-start animate-fade-in">
                  <div className="max-w-3xl mr-12">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-full w-8 h-8 flex items-center justify-center text-white font-bold text-sm">
                        AI
                      </div>
                      <span className="text-sm text-gray-500 font-medium">Assistant est en train d'écrire...</span>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-2xl px-6 py-4 shadow-md">
                      <div className="flex space-x-2">
                        <div className="w-3 h-3 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-3 h-3 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-3 h-3 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>
        )}
      </div>

      {/* Popup Contact */}
      {showContactPopup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full animate-scale-in">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Nous contacter</h3>
              <button
                onClick={() => setShowContactPopup(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-all"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-gray-600">
                Notre équipe support est disponible pour vous aider personnellement
              </p>

              <button
                onClick={() => {
                  setShowContactPopup(false);
                  navigate('/contact');
                }}
                className="w-full inline-flex items-center justify-center px-6 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold text-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
              >
                <Mail className="w-5 h-5 mr-2" />
                Ouvrir le formulaire de contact
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                🔒 Conforme RGPD • Réponse sous 24h • Support en français
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
